import { useQueryClient } from '@tanstack/react-query';

import { useCallback } from 'react';

import { useApiClient } from '@@common';

import { DataSchemaClient, DomainMetadataTargetType, DomainModelingDataModel, EnumModel, SchemaHierarchyModel, SchemaSummaryModel } from '../../../api/api';
import { DomainMetadataUpdateItem } from '../DomainGraphUtils';
import { contextDesignerQueryKeys } from '../ContextDesignerQueryKeys';

export function useUpdateDomainMetadata(contextId: string) {
	const client = useApiClient(DataSchemaClient);
	const queryClient = useQueryClient();

	const batchUpdateDomainMetadata = useCallback(
		async (items: DomainMetadataUpdateItem[]) => {
			if (items.length === 0) {
				return;
			}

			await client.batchUpdateDomainMetadata(contextId, {
				items,
			});

			const entityMetadataById = new Map(items.filter((item) => item.targetType === DomainMetadataTargetType.Entity).map((item) => [item.id, item.metadata]));
			const enumMetadataById = new Map(items.filter((item) => item.targetType === DomainMetadataTargetType.Enum).map((item) => [item.id, item.metadata]));

			if (entityMetadataById.size > 0) {
				queryClient.setQueryData<DomainModelingDataModel>(contextDesignerQueryKeys.domainModel(contextId), (current) => {
					if (!current) {
						return current;
					}

					return {
						...current,
						entities: current.entities.map((entity) => {
							const metadata = entityMetadataById.get(entity.id);
							return metadata === undefined ? entity : { ...entity, metadata };
						}),
					};
				});

				queryClient.setQueryData<SchemaSummaryModel[]>(['entities', contextId], (current) => {
					if (!current) {
						return current;
					}

					return current.map((entity) => {
						const metadata = entityMetadataById.get(entity.id);
						return metadata === undefined ? entity : { ...entity, metadata };
					});
				});

				entityMetadataById.forEach((metadata, entityId) => {
					queryClient.setQueryData<SchemaHierarchyModel | null>(['entity', contextId, entityId], (current) => {
						if (!current) {
							return current;
						}

						return { ...current, metadata };
					});
				});
			}

			if (enumMetadataById.size > 0) {
				queryClient.setQueryData<DomainModelingDataModel>(contextDesignerQueryKeys.domainModel(contextId), (current) => {
					if (!current) {
						return current;
					}

					return {
						...current,
						enums: current.enums.map((enumModel) => {
							const metadata = enumMetadataById.get(enumModel.id);
							return metadata === undefined ? enumModel : { ...enumModel, metadata };
						}),
					};
				});

				queryClient.setQueryData<EnumModel[]>(['enums', contextId], (current) => {
					if (!current) {
						return current;
					}

					return current.map((enumModel) => {
						const metadata = enumMetadataById.get(enumModel.id);
						return metadata === undefined ? enumModel : { ...enumModel, metadata };
					});
				});
			}
		},
		[client, contextId, queryClient],
	);

	return { batchUpdateDomainMetadata };
}
